import React, { useState, useEffect, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import { fetchCompletedTasks } from "../../../../service/UserDashboard";
import AppContext from "../../../../context/AppContext";
import ChartModal from "./ChartModal";
import translations from "../../../common/Translations";
import { DateFilter } from "./DateFilter";
import ActionIconButton from "../../../common/ActionIconButton";
import { BarChart3, CalendarDays, CheckCircle2, ChevronsLeft, ChevronsRight, Eye } from "lucide-react";

const ITEMS_PER_PAGE = 3;

const CompletedTask = ({ selectedOrgId }) => {
  const [data, setData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const { theme, langMode } = useContext(AppContext);
  const isRtl = langMode === "ar";
  const [showModal, setShowModal] = useState(false);
  const [chartView, setChartView] = useState("bar"); // Default chart view
  const [dateFilter, setDateFilter] = useState(false);
  const defaultColors = ["#6366F1", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#F472B6"]; // Indigo, Emerald, Amber, Red, Violet, Cyan, Pink
  const darkThemeColors = ["#60A5FA", "#34D399", "#FBBF24", "#F87171", "#A78BFA", "#22D3EE", "#FB7185"]; // Lighter shades for dark theme
  const highContrastColors = ["#FFFF00", "#00FFFF", "#FF00FF", "#00FF00", "#FFD700", "#C0C0C0", "#FFC0CB"]; // Bold for high contrast

  const getChartColors = () => {
    if (theme === "dark") return darkThemeColors;
    if (theme === "high-contrast") return highContrastColors;
    return defaultColors;
  };

  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const currentPage = Math.floor(currentIndex / ITEMS_PER_PAGE) + 1;

  useEffect(() => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, "0");
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const yyyy = today.getFullYear();
    const defaultFromDate = `${yyyy}-${mm}-${dd}T00:00`;
    const defaultToDate = `${yyyy}-${mm}-${dd}T23:59`;
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
  }, []);

  useEffect(() => {
    const getData = async () => {
      try {
        const fetchedData = await fetchCompletedTasks(
          selectedOrgId,
          fromDate,
          toDate
        );
        setData(Array.isArray(fetchedData) ? fetchedData : []);
        setCurrentIndex(0); // Reset pagination when data changes
      } catch (error) {
        console.error("Error fetching completed tasks:", error);
        setData([]); // Ensure data is reset on error
        setCurrentIndex(0);
      }
    };
    getData();
  }, [selectedOrgId, fromDate, toDate]);

  const paginatedData = () => {
    if (Array.isArray(data)) {
      return data.slice(currentIndex, currentIndex + ITEMS_PER_PAGE);
    }
    return [];
  };

  const categories = paginatedData().map(
    (item) => item.userName || translations[langMode].unknownUser || "Unknown User"
  );
  const series = paginatedData().map((item) => item.taskCount || 0);
  const radialBarCategories = categories; // Uses paginated categories
  const radialBarData = series; // Uses paginated series
  const totalCompletedTasksForPage = radialBarData.reduce((sum, count) => sum + count, 0);
  const normalizedRadialBarData = radialBarData.map(count =>
    totalCompletedTasksForPage > 0 ? (count / totalCompletedTasksForPage) * 100 : 0
  );

  const baseChartOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      foreColor: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Global text color
      fontFamily: 'inherit',
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#333")], // Data label color
      },
      formatter: function (val) {
        return val;
      }
    },
    legend: {
      show: true,
      position: "bottom",
      fontSize: "13px",
      labels: {
        colors: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Legend label color
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      enabled: true,
      theme: theme === "dark" ? "dark" : "light",
      style: {
        fontSize: "13px",
        fontFamily: "inherit",
        background: theme === "dark" ? "#333" : theme === "high-contrast" ? "#FFF" : "#f4f4f4",
        color: theme === "dark" ? "#fff" : theme === "high-contrast" ? "#000" : "#333",
      },
      y: {
        formatter: function (val) {
          return val + (val === 1 ? " task" : " tasks"); // Singular/plural
        }
      }
    },
    colors: getChartColors(), // Dynamic colors
    grid: {
      show: true,
      borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
      strokeDashArray: 4,
      position: 'back'
    }
  };

  const RadialBarChartView = ({ series, labels, paginatedOriginalData }) => { // Pass paginatedOriginalData
    const radialBarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radialBar",
        height: '100%',
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
          },
          track: {
            show: true,
            startAngle: undefined,
            endAngle: undefined,
            background: theme === "dark" ? "#374151" : "#e0e0e0",
            strokeWidth: '97%',
            opacity: 1,
            margin: 10,
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              blur: 4,
              opacity: 0.15
            }
          },
          dataLabels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 600,
              color: baseChartOptions.chart.foreColor,
              offsetY: -10
            },
            value: {
              show: true,
              fontSize: '18px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 700,
              color: baseChartOptions.chart.foreColor,
              offsetY: 5,
              formatter: function (val, { seriesIndex, dataPointIndex, w }) {
                // Display the actual count for the specific series from the paginated data
                const originalTaskCount = paginatedOriginalData[seriesIndex]?.taskCount || 0;
                return (translations[langMode].tasksCountLabel || "{n} tasks").replace("{n}", originalTaskCount);
              }
            },
            total: {
              show: true,
              label: translations[langMode].totalCompletedTasks || "Completed Tasks",
              color: baseChartOptions.chart.foreColor,
              formatter: function (w) {
                return paginatedOriginalData.reduce((a, b) => a + (b.taskCount || 0), 0);
              }
            }
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: getChartColors(),
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      labels: labels, // These labels will appear in the legend
      stroke: {
        lineCap: 'round'
      },
      legend: {
        show: true,
        position: 'bottom',
        offsetY: 0,
        height: 50,
        markers: {
          fillColors: getChartColors()
        }
      },
    };

    return (
      <div id="completed-radial-bar-chart" className="w-full h-full">
        <ReactApexChart
          options={radialBarOptions}
          series={series} // This should be the normalized percentage data
          type="radialBar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const DonutChartView = ({ series, categories }) => {
    const donutOptions = {
      ...baseChartOptions,
      labels: categories,
      dataLabels: {
        ...baseChartOptions.dataLabels,
        formatter: function (val, opts) {
          const total = opts.w.globals.seriesTotals.reduce((a, b) => a + b, 0);
          return total > 0 ? `${val.toFixed(1)}%` : ''; // Show percentage if total > 0
        },
        style: {
          fontSize: "10px", // Smaller font for donut labels
          colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#333")],
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "14px",
                color: baseChartOptions.chart.foreColor,
              },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (val) {
                  return val;
                },
              },
              total: {
                show: true,
                showAlways: true,
                label: translations[langMode].totalTasks || "Total Tasks (Current Page)",
                fontSize: '14px',
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0);
                }
              },
            },
          },
        },
      },
      stroke: {
        show: true,
        colors: [theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff")],
        width: 2
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + (val === 1 ? " task" : " tasks");
          }
        }
      }
    };

    return (
      <div id="chart" className="w-full h-full">
        <ReactApexChart
          options={donutOptions}
          series={series}
          type="donut"
          width="100%" // Make it responsive
          height="100%" // Make it responsive
        />
      </div>
    );
  };

  const BarChartView = ({ series, categories }) => {
    const barOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "bar",
        // Background color for the chart area
        background: theme === "dark" ? "#1F2937" : "#FFFFFF",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '13px', // Slightly larger font size for labels
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
        axisTicks: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '13px', // Slightly larger font size for labels
            fontWeight: 500,
          },
          formatter: function (val) {
            return Math.floor(val); // Ensure whole numbers for user counts
          }
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        title: {
          text: translations[langMode].completedTaskAxis || "Completed Task",
          style: {
            color: baseChartOptions.chart.foreColor,
            fontSize: '14px',
            fontWeight: 600,
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 8, // More rounded corners for a softer look
          columnWidth: '45%', // Slightly thinner columns
          distributed: true, // Distribute colors across categories
          dataLabels: {
            position: 'top',
            offsetY: -20, // Adjust offset to prevent overlap
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20, // Position data labels above the bars
        style: {
          fontSize: '13px',
          colors: [theme === "dark" ? "#FFF" : "#333"], // Ensure good contrast
          fontWeight: 600,
        },
        background: {
          enabled: true,
          foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
          padding: 6,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          }
        },
        formatter: function (val) {
          return val; // Display the raw count
        }
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4, // Dashed grid lines
        position: 'back',
        xaxis: {
          lines: {
            show: false // No vertical grid lines for cleaner look
          }
        },
        yaxis: {
          lines: {
            show: true // Keep horizontal grid lines for readability
          }
        },
        padding: {
          left: 0,
          right: 0
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return val + " users";
          }
        },
        x: {
          formatter: function (val) {
            return val;
          }
        },
      },
      colors: getChartColors(), // Apply dynamic colors
      legend: {
        show: false // Hide legend for single series bar chart
      },
      // Adding responsive options for better scaling
      responsive: [{
        breakpoint: 768, // Adjust for medium screens and smaller
        options: {
          plotOptions: {
            bar: {
              columnWidth: '60%',
            }
          },
          dataLabels: {
            offsetY: -15,
            style: {
              fontSize: '11px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            }
          }
        }
      },
      {
        breakpoint: 480, // Adjust for small screens
        options: {
          plotOptions: {
            bar: {
              columnWidth: '70%',
            }
          },
          dataLabels: {
            offsetY: -10,
            style: {
              fontSize: '10px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            }
          }
        }
      }
      ]
    };

    return (
      <div id="user-bar-chart" className="w-full h-full "> {/* Added padding */}
        <ReactApexChart
          options={barOptions}
          series={[{ name: translations[langMode].completedTaskAxis || "Completed Task", data: series }]}
          type="bar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const RadarChartView = ({ series, categories }) => {
    const radarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radar",
        height: '100%',
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      yaxis: {
        show: false, // Often hidden for radar charts for cleaner look
      },
      stroke: {
        width: 2,
        curve: "smooth",
      },
      fill: {
        opacity: 0.4,
      },
      markers: {
        size: 5,
        hover: {
          size: 7,
        },
        colors: getChartColors(),
        strokeColors: theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff"),
        strokeWidth: 2,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: [baseChartOptions.chart.foreColor]
        },
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
    };

    return (
      <div id="radar-chart" className="w-full h-full">
        <ReactApexChart
          options={radarOptions}
          series={[{ name: translations[langMode].taskCount || "Task Count", data: series }]}
          type="radar"
          width="100%" // Make it responsive
          height="100%" // Make it responsive
        />
      </div>
    );
  };

  const AreaChartView = ({ series, categories }) => {
    const areaOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "area",
        height: '100%',
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
        tooltip: {
          enabled: false // Tooltip for X-axis not typically needed for area charts
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
        title: {
          text: translations[langMode].taskCount || "Task Count",
          style: {
            color: baseChartOptions.chart.foreColor,
          }
        }
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100]
        }
      },
      dataLabels: {
        enabled: true,
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
    };

    return (
      <div id="area-chart" className="w-full h-full">
        <ReactApexChart
          options={areaOptions}
          series={[{ name: translations[langMode].taskCount || "Task Count", data: series }]}
          type="area"
          width="100%" // Make it responsive
          height="100%" // Make it responsive
        />
      </div>
    );
  };


  const handleNext = () => {
    if (currentIndex + ITEMS_PER_PAGE < data.length) {
      setCurrentIndex(currentIndex + ITEMS_PER_PAGE);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - ITEMS_PER_PAGE);
    }
  };

  const isDataEmpty = data.length === 0 || data.every(item => item.taskCount === 0);

  return (
    <div className={`h-full w-full flex flex-col ${theme === "dark" ? "text-gray-200" : "text-gray-900"} p-2`}>
      <div className="flex items-center justify-between py-1 w-full gap-2">
        <h2 className="font-semibold text-xs sm:text-base text-left flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
          <CheckCircle2 size={18} className="text-green-500 shrink-0 sm:w-5 sm:h-5" />
          <label className="mt-0.5 truncate">{translations[langMode].completedTaskCount}</label>
        </h2>
      
          <div className="flex items-center gap-1 shrink-0"> 
            <ActionIconButton
              type="button"
              tone="gray"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              onClick={() => setDateFilter(!dateFilter)}
              aria-label={translations[langMode].RangeFilter}
              tooltipText={translations[langMode].RangeFilter}
              tooltipPosition="bottom"
            >
              <CalendarDays size={18} />
            </ActionIconButton>
            <ActionIconButton
              type="button"
              tone="gray"
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              onClick={() => setShowModal(true)}
              aria-label={translations[langMode].ViewChart}
              tooltipText={translations[langMode].ViewChart}
              tooltipPosition="bottom"
            >
              <Eye size={18} />
            </ActionIconButton>
          </div>
      
      </div>

      {!selectedOrgId ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === "dark"
                ? "bg-[#236DB4]/20 text-[#236DB4]"
                : theme === "high-contrast"
                  ? "bg-black text-yellow-300"
                  : "bg-[#236DB4]/10 text-[#236DB4]"
              }`}
          >
            <BarChart3 className="h-6 w-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
              {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
            </p>
            <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
              {translations[langMode].chartWaitingForResponses}
            </p>
          </div>
        </div>
      ) : isDataEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === "dark"
                ? "bg-[#236DB4]/20 text-[#236DB4]"
                : theme === "high-contrast"
                  ? "bg-black text-yellow-300"
                  : "bg-[#236DB4]/10 text-[#236DB4]"
              }`}
          >
            <BarChart3 className="h-6 w-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
              {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
            </p>
            <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
              {translations[langMode].chartWaitingForResponses}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 w-full flex flex-col justify-between"> 
          <div className="flex-1 w-full h-full "> 
            {chartView === "chart" ? ( 
              <RadialBarChartView series={normalizedRadialBarData} labels={radialBarCategories} paginatedOriginalData={paginatedData()} />
            ) : chartView === "donut" ? (
              <DonutChartView series={series} categories={categories} />
            ) : chartView === "area" ? (
              <AreaChartView series={series} categories={categories} />
            ) : chartView === "radar" ? (
              <RadarChartView series={series} categories={categories} />
            ) : ( 
              <BarChartView series={series} categories={categories} />
            )}
          </div>
          {totalPages > 1 && (
            <div className="gap-2 flex justify-between items-center flex-shrink-0"> {/* Added justify-center and items-center */}
              <ActionIconButton
                onClick={handlePrev}
                disabled={currentIndex === 0}
                tone="blue"
                className={`${currentIndex === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'
                  }`}
                aria-label="Previous page"
              >
                {isRtl ? (
                  <ChevronsRight size={18} />
                ) : (
                  <ChevronsLeft size={18} />
                )}
              </ActionIconButton>
              <span className={`text-sm font-medium ${theme === "dark" ? "text-gray-300" : "text-gray-700"}`}>
                {translations[langMode].page || "Page"} {currentPage} {translations[langMode].of || "of"} {totalPages}
              </span>
              <ActionIconButton
                onClick={handleNext}
                disabled={currentIndex + ITEMS_PER_PAGE >= data.length}
                tone="blue"
                className={`${currentIndex + ITEMS_PER_PAGE >= data.length
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-500 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-700'
                  }`}
                aria-label="Next page"
              >
                {isRtl ? (
                  <ChevronsLeft size={18} />
                ) : (
                  <ChevronsRight size={18} />
                )}
              </ActionIconButton>
            </div>
          )}
        </div>
      )}
      <ChartModal
        showModal={showModal}
        setShowModal={setShowModal}
        setChartView={setChartView}
        currentChartView={chartView}
        availableChartTypes={["radialBar", "bar", "area", "radar", "donut"]} // Ensure donut is also available
      />
      <DateFilter
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        setFromDate={setFromDate}
        fromDate={fromDate}
        setToDate={setToDate}
        toDate={toDate}
      />
    </div>
  );
};

export default CompletedTask;
